import { Conditional, useBreakpoint } from 'components/pcc-components';
import { CatCard, CatHeading, CatIconAdd } from 'components/blocks-components';
import { PAGE_BREAKPOINTS, PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import Styles from './fbt-grid/fbt-grid.module.scss';
import { AllFbtItems, CurrentItems } from 'services/fbtItems/types';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import Image from 'next/image';
import FbtTitle from './fbt-title';
import { FBTMobileDrawer } from './fbt-mobile-drawer';
import { FBTDrawerContent } from './fbt-mobile-drawer/fbt-mobile-drawer-content';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { FbtCurrentItemContent } from './fbt-mobile-drawer/fbt-mobile-drawer-content/fbt-current-item-content';
import { FBTMobileDrawerProvider } from './fbt-mobile-drawer/fbt-mobile-drawer-context';
import { subTotalResponse } from 'services/subTotal/types';
import FbtSubtotalAction from './fbt-subtotal';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { FBT_LIST_NAME } from '../../constants/constants';
import { CartResponse } from 'services/cart/types';
interface FbtMobileProps {
  currentItem: CurrentItems;
  products: AllFbtItems[];
  frequestBoughtMobile: boolean;
  currency: string;
  isCatCorp: boolean;
  partsPriceData: ProductDealerPriceAvailability[];
  contactDealerComp?: React.ReactNode;
  contactDealerBtn?: React.ReactNode;
  subTotal?: subTotalResponse;
  setSubTotal?: (arg: {
    formattedSubtotal: string;
    unformattedSubtotal: number;
  }) => void;
  totalLoading?: boolean;
  itemSummary: ISummary;
  storeLocationId: string;
  cartId: string;
  setTotalLoading?: (arg: boolean) => void;
  cartDetails?: CartResponse;
  handleCheckboxChange: (
    e: CustomEvent<{ checked: boolean; indeterminate: boolean; value: string }>
  ) => void;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

type MergedProductPrice = ProductDealerPriceAvailability & {
  type: string;
  name: string;
  imageURL: string;
};

export const FrequentlyBoughtTogetherMobile: FC<FbtMobileProps> = ({
  currentItem,
  products,
  frequestBoughtMobile = false,
  currency,
  isCatCorp,
  partsPriceData,
  subTotal,
  setSubTotal,
  totalLoading,
  setTotalLoading,
  handleCheckboxChange,
  contactDealerComp,
  contactDealerBtn,
  itemSummary,
  storeLocationId,
  cartId,
  cartDetails,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const { partNumber = '', imageURL = '' } = currentItem || {};
  const isGAEventTriggered = useRef(false);
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const mergeFBTItems = (partsData, summary, isCatCorp) => {
    if (!partsData || partsData.length === 0) {
      return (
        summary.products?.map((product) => ({
          ...product,
          totalPrice: '',
          unformattedTotalPrice: 0,
          enableAddToCart: false,
          availability: '',
          manufacturer: summary.products[0]?.manufacturer
        })) || []
      );
    }
    return partsData.map((item) => {
      const matched = summary.products.find(
        (priceItem) => priceItem.partNumber === item.partNumber
      );
      return {
        ...item,
        ...matched,
        manufacturer: summary.products[0]?.manufacturer
      };
    });
  };

  const mergedFbtResponse = useMemo(
    () => mergeFBTItems(partsPriceData, itemSummary, isCatCorp),
    [partsPriceData, itemSummary, isCatCorp]
  ) as MergedProductPrice[];

  const filteredFbtResponse = useMemo(() => {
    const currentItemPartNumber = currentItem?.partNumber;
    return mergedFbtResponse.filter(
      (item) => item.partNumber !== currentItemPartNumber
    );
  }, [mergedFbtResponse, currentItem?.partNumber]);

  const orderedFBTItems = itemSummary?.products
    .filter((product) =>
      filteredFbtResponse.some((fbt) => fbt.partNumber === product.partNumber)
    )
    .map((product) => {
      const matchedFbt = filteredFbtResponse.find(
        (fbt) => fbt.partNumber === product.partNumber
      );
      return matchedFbt || product;
    });

  const fbtProducts = (orderedFBTItems ?? [])?.slice(0, 2);
  const maxCountToBeShown = fbtProducts?.length - 1;

  const getUpdatedCurrentItem = (
    currentItem: CurrentItems,
    partsPriceData: ProductDealerPriceAvailability[]
  ) => {
    const currentItemPriceData = partsPriceData.find(
      (item) => item.partNumber === currentItem.partNumber
    );

    return {
      ...currentItem,
      unformattedUnitPrice: currentItemPriceData?.unformattedTotalPrice,
      price: currentItemPriceData?.totalPrice
    };
  };

  const getFbtViewItemList = (currency: string) => ({
    item_list_id: FBT_LIST_NAME,
    item_list_name: FBT_LIST_NAME,
    currency: currency
  });

  const fireGAEvent = useCallback(
    (forViewAllParts = false) => {
      const fbtViewItemList = getFbtViewItemList(currency);

      const updatedCurrentItem = getUpdatedCurrentItem(
        currentItem,
        partsPriceData
      );

      const selectedProducts = forViewAllParts
        ? [updatedCurrentItem, ...orderedFBTItems]
        : [updatedCurrentItem, ...fbtProducts];

      fireViewItemListEvent({
        itemListData: fbtViewItemList,
        locationId: storeLocationId,
        products: selectedProducts,
        cartId: cartId,
        cartItemsData: cartDetails,
        isProductsData: true
      });
    },
    [
      fireViewItemListEvent,
      orderedFBTItems,
      storeLocationId,
      fbtProducts,
      cartId,
      cartDetails,
      currency,
      currentItem,
      partsPriceData
    ]
  );

  useEffect(() => {
    if (isMD === false && !isGAEventTriggered.current && cartId !== null) {
      fireGAEvent(false);
      isGAEventTriggered.current = true;
    }
  }, [fireGAEvent, cartId, isMD]);
  return (
    <Conditional test={frequestBoughtMobile}>
      <CatCard className="d-md-none">
        <div className="d-flex flex-column gap-3">
          <FbtTitle />
          <div className="d-flex flex-row justify-content-between">
            <div className="d-flex flex-column">
              <Image
                src={imageURL || PRODUCT_FALLBACK_IMAGE}
                alt={partNumber}
                className={Styles['c-fbt-card__image']}
                loading="lazy"
                width={80}
                height={80}
                unoptimized
              />
              <CatHeading variant="footnote" className="align-self-center">
                {partNumber}
              </CatHeading>
            </div>
            <CatIconAdd className="align-self-center" />
            {fbtProducts?.map((fbtItem: AllFbtItems, index: number) => {
              const { imageURL = '', partNumber = '' } = fbtItem || {};
              return (
                <div
                  key={partNumber}
                  className="d-flex flex-row justify-content-between gap-2"
                >
                  <div className="d-flex flex-column" key={partNumber}>
                    <Image
                      src={imageURL || PRODUCT_FALLBACK_IMAGE}
                      alt={partNumber}
                      className={Styles['c-fbt-card__image']}
                      loading="lazy"
                      width={80}
                      height={80}
                      unoptimized
                    />
                    <CatHeading
                      variant="footnote"
                      className="align-self-center"
                    >
                      {partNumber}
                    </CatHeading>
                  </div>
                  {index < maxCountToBeShown ? (
                    <CatIconAdd className="align-self-center" />
                  ) : (
                    <></>
                  )}
                </div>
              );
            })}
          </div>
          <FBTMobileDrawerProvider>
            <FBTMobileDrawer fireGAEventForViewAllParts={fireGAEvent}>
              <FbtCurrentItemContent
                currentItem={currentItem}
                partsPriceData={partsPriceData}
                currency={currency}
                isCatCorp={isCatCorp}
                frequestBoughtMobile={frequestBoughtMobile}
                handleCheckboxChange={handleCheckboxChange}
                contactDealerComp={contactDealerBtn}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
                cartId={cartId}
                storeLocationId={storeLocationId}
              />
              <FBTDrawerContent
                products={products}
                isCatCorp={isCatCorp}
                partsPriceData={partsPriceData}
                currency={currency}
                frequestBoughtMobile={frequestBoughtMobile}
                handleCheckboxChange={handleCheckboxChange}
                contactDealerComp={contactDealerBtn}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
                cartId={cartId}
                storeLocationId={storeLocationId}
              />
              <FbtSubtotalAction
                isCatCorp={isCatCorp}
                partsPriceData={partsPriceData}
                subTotal={subTotal}
                currency={currency}
                currentItem={currentItem}
                setSubTotal={setSubTotal}
                setTotalLoading={setTotalLoading}
                isFrequentlyBoughtMobile={frequestBoughtMobile}
                contactDealerComp={contactDealerComp}
                itemSummary={itemSummary}
                storeLocationId={storeLocationId}
                cartId={cartId}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
              />
            </FBTMobileDrawer>
          </FBTMobileDrawerProvider>
        </div>
      </CatCard>
    </Conditional>
  );
};
